<script>
  import { createEventDispatcher } from 'svelte';

  import { getWalletClient, getPublicClient, writeContract } from '@wagmi/core';

  import { Form, _ } from 'lt-form';

  import { getContractValue, getContractJSONvalue } from '../utils.js';

  import { getABI, getBytecode, updateTxs } from '../utils.js';
  import { chainId, walletAddress } from '../stores.js';
  import { db } from '../db.js';

  import * as setupFunctions from '../setupContracts.js'

  export let openapi;
  export let schema;
  export let layout = undefined;
  export let value;
  export let setupData = undefined;
  export let contractType = undefined;
  export let chain = undefined;
  export let contract = undefined;
  export let updateFunction = undefined;
  export let tokenId = undefined;
  export let oracle = undefined;
  export let extraUi = undefined;
  export let apiurl = undefined;
  export let apimethod = undefined;
  export let apiheaders = undefined;
  export let apierrorcallback = undefined;
  export let texts = undefined;
  export let extras = undefined;

  export const reset = () => {
    for (const field of Object.values(dependencies)) {
      if (schema.properties[field].format === 'tokens') {
        delete schema.properties[field].enum
      }
    }
    schema = schema;
    form.reset()
  }

  let form;
  let lastValue;
  let dependencies = {};

  $: $walletAddress, init();
  $: value, checkFields();

  const init = async () => {
    if ($walletAddress) {
      if (typeof openapi === 'string') {
        const resp = await fetch(openapi);
        if (resp.ok) {
          openapi = await resp.json();
          if (extraUi) {
            for (const [schema, ui] of Object.entries(extraUi)) {
              openapi.components.schemas[schema].ui = { ...openapi.components.schemas[schema].ui, ...ui }
            }
          }
        }
      }

      if (typeof schema === 'string') {
        schema = openapi.components.schemas[schema];
      }

      chain = chain || $chainId;
      for (const [ propName, prop ] of Object.entries(schema.properties)) {
        if (prop.format === 'contracts') {
          await updateTxs(chain, $walletAddress);
          const collections = await db.txs.where({ chain, contractType: 'Collection2' }).toArray();
          const contracts = [];
          for (const c of collections) {
            contracts.push({
              value: c.contractAddress, label: await getContractValue(chain, c.contractAddress, 'name')
            });
          }
          prop.enum = contracts;
        } else if (prop.format === 'tokens') {
          dependencies[prop.contractField] = propName;
        }
      }
      schema = schema;
    }
  }

  const checkFields = async () => {
    if (value) {
      for (const [ trigger, field ] of Object.entries(dependencies)) {
        if ((!lastValue && value[trigger]) || value[trigger] !== lastValue[trigger]) {
          if (schema.properties[field].format === 'tokens') {
            value[field] = undefined;
            schema.properties[field].enum = [ _('Loading', texts) ];
            schema = schema;
            chain = chain || $chainId;
            const tokensAmount = await getContractValue(chain, value[trigger], 'tokensAmount');
            const tokens = [];
            for (let idx = 0; idx < Number(tokensAmount); idx++) {
              tokens.push({
                value: idx, label: await  getContractJSONvalue(chain, value[trigger], idx, 'name')
              });
            }
            schema.properties[field].enum = tokens;
            schema = schema;
          }
        }
      }
      lastValue = Object.assign({}, value);
    }
  }

  const deploy = async (data, fee) => {
    const walletClient = await getWalletClient({ chainId: chain });
    const opts = {
      abi: await getABI(contractType),
      bytecode: await getBytecode(contractType),
      address: walletClient.account.address,
      args: data
    }
    if (fee) opts.value = fee;

    const hash = await walletClient.deployContract(opts);
    dispatch('hashed', hash);
    new CustomEvent('hashed', { detail: hash });
    const receipt = await getPublicClient().waitForTransactionReceipt({ hash });
    dispatch('deployed', receipt)
    new CustomEvent('deployed', { detail: receipt });
  }

  const updateContract = async data => {
    const { hash } = await writeContract({
      address: contract, abi: await getABI(contract), functionName: updateFunction, args: data
    });
    dispatch('hashed', hash);
    new CustomEvent('hashed', { detail: hash });
    const receipt = await getPublicClient().waitForTransactionReceipt({ hash });
    dispatch('updated', receipt)
    new CustomEvent('updated', { detail: receipt });
  }

  const submit = async ({ detail }) => {
    if (contractType) {
      const setupD = typeof setupData === 'string' ? setupFunctions[setupData] : setupData;
      const { data, fee } = await setupD(chain, detail, oracle);
      console.log(data)
      try {
        await deploy(data, fee);
      } catch (err) {
        if (err.shortMessage === 'User rejected the request.') {
          dispatch('user-cancel');
          new CustomEvent('user-cancel');
          loading = false;
        } else {
          console.log(err)
        }
      }
    } else if (updateFunction) {
      if (setupData) {
        const setupD = typeof setupData === 'string' ? setupFunctions[setupData] : setupData;
        var { data } = await setupD(chain, contract, detail);
      } else {
        data = detail
      }
      try {
        await updateContract(data);
      } catch (err) {
        if (err.shortMessage === 'User rejected the request.') {
          dispatch('user-cancel');
          new CustomEvent('user-cancel');
        } else {
          console.log(err)
          dispatch('error', err);
          new CustomEvent('error', { detail: err });
        }
      }
    } else if (tokenId) {
      const setupD = typeof setupData === 'string' ? setupFunctions[setupData] : setupData;
      const { data, fee } = await setupD(chain, contract, tokenId, detail);
      console.log(data, fee);
    } else if (contract) {
      const setupD = typeof setupData === 'string' ? setupFunctions[setupData] : setupData;
      const { data } = await setupD(chain, contract, detail);
      const update = typeof updateContract === 'string' ? setupFunctions[setupData] : updateContract;
      await update(data);
    } else {
      dispatch('submitted', data)
      document.dispatchEvent(new CustomEvent('submitted', { detail: data }));
    }
  }

  const dispatch = createEventDispatcher();
</script>

<Form
  { openapi } { schema } { layout } apiURL={ apiurl } apiMethod={ apimethod } apiHeaders={ apiheaders } apiErrorCallback={ apierrorcallback } { texts } { extras }
  bind:this={ form } bind:value={ value }
  on:submit={ submit } on:cancel={ () => { dispatch('cancel') } }>

  <svelte:fragment slot="array-widgets" let:addArrayWidget={ addArrayWidget } let:removeArrayWidget={ removeArrayWidget }>
    <slot name="array-widgets" { addArrayWidget } { removeArrayWidget }></slot>
  </svelte:fragment>
  <svelte:fragment slot="wallet-yours" let:value={ value } let:setValue={ setValue }>
    <slot name="wallet-yours" { value } { setValue } />
  </svelte:fragment>

</Form>
